/**
 * Main application layout component
 * 
 * @author Claude
 */
import { ReactNode } from 'react';
import { Box, Container, CssBaseline } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { SkipLink } from './SkipLink';
import { useRTL } from '../../hooks/useRTL';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface LayoutProps {
  children?: ReactNode;
  className?: string;
  title?: string;
  breadcrumbs?: Breadcrumb[];
  loading?: boolean;
  error?: Error;
  sidebar?: ReactNode;
  fullWidth?: boolean;
}

/**
 * Main application layout with header, content area, and footer
 * @param {LayoutProps} props - Component props
 * @returns {JSX.Element} Layout component with children or outlet
 * 
 * @a11y Includes skip link for keyboard navigation
 * @a11y Uses semantic HTML structure with header, main, and footer elements
 * @a11y Main content has proper heading structure
 */
export const Layout = ({
  children,
  className,
  title,
  breadcrumbs,
  loading,
  error,
  sidebar,
  fullWidth
}: LayoutProps) => {
  const { direction } = useRTL();

  // Update document title if provided
  if (title) {
    document.title = title;
  }

  return (
    <Box
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        direction,
      }}
    >
      <CssBaseline />
      <SkipLink />
      <Header />

      {breadcrumbs && (
        <Box role="navigation" aria-label="breadcrumb" sx={{ px: 2, py: 1 }}>
          {breadcrumbs.map((crumb, index) => (
            <span key={index}>
              {crumb.href ? (
                <a href={crumb.href}>{crumb.label}</a>
              ) : (
                crumb.label
              )}
              {index < breadcrumbs.length - 1 && ' > '}
            </span>
          ))}
        </Box>
      )}

      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          pt: { xs: 10, sm: 12 }, // Account for header height + spacing
          pb: { xs: 2, sm: 3 },
          maxWidth: fullWidth ? 'none' : undefined,
        }}
      >
        {loading && (
          <Box role="progressbar" sx={{ textAlign: 'center', py: 4 }}>
            Loading...
          </Box>
        )}

        {error && (
          <Box role="alert" sx={{ color: 'error.main', p: 2 }}>
            {error.message}
          </Box>
        )}

        <Container maxWidth={fullWidth ? false : "lg"}>
          {sidebar ? (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ minWidth: 200 }}>{sidebar}</Box>
              <Box sx={{ flexGrow: 1 }}>{children || <Outlet />}</Box>
            </Box>
          ) : (
            children || <Outlet />
          )}
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};